import {useCallback, useMemo, useState} from 'react';
import {useNavigate, useParams} from 'react-router-dom';
import {Box, Button, CardContent, Typography} from '@mui/material';
import {
  DataGridPro,
  GridColDef,
  GridRenderCellParams,
} from '@mui/x-data-grid-pro';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import CreateButton from '../../../../components/Button/CreateButton';
import MsgAddIcon from '../../../../asset/icons/addmsg.svg';
import {IDemoAdSpotProps} from '../../../../dto/demoStingerFulfilment.dto';
import DialogAdSpot from './DialogAdSpot';
import MusicPreviewBtn, {
  MusicPreviewBtnDisable,
} from '../../../../components/music/MusicPreviewBtn';
import {apiPost} from '../../../../axiosConfig';
import DialogApiRequestStatus from '../../../../components/dialogs/DialogApiRequestStatus';

type Props = {
  id: number;
  adSpotObject: IDemoAdSpotProps[];
  setAdSpotObject: (val: IDemoAdSpotProps[]) => void;
};
const DeleteBlock = ({id, adSpotObject, setAdSpotObject}: Props) => {
  const handleDelete = (id: number) => {
    const updatedAdSpots = adSpotObject.filter(adSpot => adSpot.id !== id);
    setAdSpotObject(updatedAdSpots);
  };
  return (
    <DeleteOutlineIcon
      onClick={() => {
        handleDelete(id);
      }}
    />
  );
};
const DemoStingerAdSpot = () => {
  const task = useParams();
  const navigate = useNavigate();
  const taskId = Number(task.task_id);
  const [files, setFiles] = useState<File[]>([]);
  const [adSpot, setAdSpot] = useState<IDemoAdSpotProps>({
    id: Date.now(),
    name: '',
    adspot: '',
    file: null,
  });
  const [adSpotObject, setAdSpotObject] = useState<IDemoAdSpotProps[]>([]);
  const [apiState, setApiState] = useState({status: '', error: null});
  const [open, setOpen] = useState(false);

  const handleAddMessage = useCallback(() => {
    setOpen(true);
  }, []);
  const columns: GridColDef[] = useMemo(
    () =>
      [
        {
          field: 'name',
          headerName: 'Script Name',
          sortable: true,
          flex: 1,
        },
        {
          field: 'adspot',
          headerName: 'AdScript',
          sortable: true,
          flex: 1,
        },
        {
          field: '',
          headerName: 'Play',
          sortable: false,
          type: 'actions',
          width: 54,
          renderCell: (params: GridRenderCellParams) => {
            if (params.row.file) {
              const file = params.row.file[0];
              if (file) {
                const url = URL.createObjectURL(file);
                return (
                  <MusicPreviewBtn
                    id={params.row.id}
                    url={url}
                    track_name={file.name || params.row?.name}
                    track_artist={file.name || ''}
                  />
                );
              }
            }
            return <MusicPreviewBtnDisable />;
          },
        },
        {
          field: 'actions',
          type: 'actions',
          headerName: 'Action',
          hideable: false,
          sortable: false,
          width: 80,
          renderCell: (params: GridRenderCellParams) => {
            return (
              <DeleteBlock
                id={params.row.id}
                adSpotObject={adSpotObject}
                setAdSpotObject={setAdSpotObject}></DeleteBlock>
            );
          },
        },
      ].map(column => ({...column, filterable: false})),
    [adSpotObject],
  );
  const handleAdd = useCallback(() => {
    setOpen(false);
    setAdSpotObject(prevAdSpotObject => [...prevAdSpotObject, adSpot]);
    setFiles([]);
    setAdSpot({
      id: Date.now(),
      name: '',
      adspot: '',
      file: null,
    });
  }, [adSpot]);
  const handleCancel = useCallback(() => {
    setOpen(false);
    setFiles([]);
    setAdSpot({
      id: Date.now(),
      name: '',
      adspot: '',
      file: null,
    });
  }, []);
  const handleSubmit = useCallback(() => {
    const fd = new FormData();
    setApiState({
      status: 'pending',
      error: null,
    });
    adSpotObject.forEach((val, index) => {
      fd.append(`scripts[${index}][name]`, val.name);
      fd.append(`scripts[${index}][adscript]`, val.adspot);
      if (val.file !== null) {
        val.file.forEach(file => {
          fd.append(`scripts[${index}][file]`, file);
        });
      }
    });
    apiPost(`/v1/task/${taskId}/demo-stinger/adscript`, fd)
      .then(() => {
        setApiState({
          status: 'success',
          error: null,
        });
      })
      .catch(error => {
        setApiState({
          status: 'error',
          error,
        });
      });
  }, [adSpotObject, taskId]);
  const handleClose = useCallback(() => {
    navigate(-1);
  }, [navigate]);
  return (
    <Box className='p-0 5 0 0'>
      <Typography>
        <Box
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
          }}>
          <Typography variant='h4'>Demo Stinger AdSpot</Typography>
          <CreateButton
            title='Add Message'
            icon={MsgAddIcon}
            onClick={handleAddMessage}
          />
        </Box>
      </Typography>
      <CardContent style={{height: '400px'}}>
        <DataGridPro
          rows={adSpotObject ?? []}
          columns={columns}
          disableColumnMenu
          disableRowSelectionOnClick
        />
        {open && (
          <DialogAdSpot
            open={open}
            setOpen={setOpen}
            files={files}
            setFiles={setFiles}
            adSpot={adSpot}
            setAdSpot={setAdSpot}
            handleAdd={handleAdd}
            handleCancel={handleCancel}
          />
        )}
      </CardContent>
      <Box className='flex justify-center '>
        <Button
          variant='contained'
          onClick={handleSubmit}
          disabled={adSpotObject.length === 0}>
          Submit AdScript
        </Button>
      </Box>
      {apiState.status !== '' && (
        <DialogApiRequestStatus
          apiState={apiState}
          onRetry={handleSubmit}
          onEdit={handleClose}
          onClose={handleClose}
        />
      )}
    </Box>
  );
};

export default DemoStingerAdSpot;
